// frontend/src/pages/WalletPage.js - Deposit, withdraw, payment methods & transactions
import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Wallet, Plus, Trash2, CreditCard, ArrowDownCircle,
  ArrowUpCircle, CheckCircle, AlertCircle, Clock, RefreshCw, Star
} from 'lucide-react';
import { formatNaira } from '../utils/money';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Matches PaymentProvider enum on the backend (and mobile's mapProviderToEnum)
const PROVIDERS = [
  { id: 'paystack', enum: 3, name: 'Paystack' },
  { id: 'flutterwave', enum: 4, name: 'Flutterwave' },
];

// Aligned with mobile APP_CONFIG limits
const LIMITS = {
  MIN_DEPOSIT: 100,
  MAX_DEPOSIT: 1000000,
  MIN_WITHDRAWAL: 500,
  MAX_WITHDRAWAL: 500000,
};

const QUICK_AMOUNTS = [500, 1000, 2000, 5000, 10000];

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('playerbet_token')}`,
});

const WalletPage = ({ user, onBack }) => {
  const [activeTab, setActiveTab] = useState('deposit');
  const [wallet, setWallet] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Deposit state
  const [depositAmount, setDepositAmount] = useState('');
  const [depositProvider, setDepositProvider] = useState('paystack');
  const [depositSubmitting, setDepositSubmitting] = useState(false);

  // Withdrawal state
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAccountId, setWithdrawAccountId] = useState('');
  const [withdrawPin, setWithdrawPin] = useState('');
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);

  // Set-PIN state (shown when wallet has no PIN yet)
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinSubmitting, setPinSubmitting] = useState(false);

  // Add bank account state
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [addProvider, setAddProvider] = useState('paystack');
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [selectedBankCode, setSelectedBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifiedName, setVerifiedName] = useState('');
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);

  // Shared feedback
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }

  const showMessage = (type, text) => {
    setMessage({ type, text });
    window.clearTimeout(showMessage._t);
    showMessage._t = window.setTimeout(() => setMessage(null), 6000);
  };

  const loadWallet = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/wallet/${user.id}`, { headers: authHeaders() });
      if (response.ok) {
        setWallet(await response.json());
      }
    } catch (error) {
      console.error('Error loading wallet:', error);
    }
  }, [user.id]);

  const loadBankAccounts = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/bank-accounts`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        const accounts = data.bankAccounts || [];
        setBankAccounts(accounts);
        const defaultAccount = accounts.find(a => a.isDefault) || accounts[0];
        if (defaultAccount) {
          setWithdrawAccountId(prev => prev || String(defaultAccount.id));
        }
      }
    } catch (error) {
      console.error('Error loading bank accounts:', error);
    }
  }, []);

  const loadTransactions = useCallback(async () => {
    setLoadingTransactions(true);
    try {
      const response = await fetch(`${API_BASE_URL}/payment/transactions`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  }, []);

  useEffect(() => {
    loadWallet();
    loadBankAccounts();
    loadTransactions();
  }, [loadWallet, loadBankAccounts, loadTransactions]);

  // Load banks list when the add-account form is open or its provider changes
  useEffect(() => {
    if (!showAddAccount) return;
    const providerEnum = PROVIDERS.find(p => p.id === addProvider)?.enum || 3;
    setLoadingBanks(true);
    setSelectedBankCode('');
    setVerifiedName('');
    fetch(`${API_BASE_URL}/bank-accounts/banks?provider=${providerEnum}`, { headers: authHeaders() })
      .then(response => (response.ok ? response.json() : { banks: [] }))
      .then(data => setBanks(data.banks || []))
      .catch(error => {
        console.error('Error loading banks:', error);
        setBanks([]);
      })
      .finally(() => setLoadingBanks(false));
  }, [showAddAccount, addProvider]);

  // Auto-verify the account once we have a bank + 10-digit account number
  useEffect(() => {
    setVerifiedName('');
    if (!selectedBankCode || accountNumber.length !== 10) return;

    const providerEnum = PROVIDERS.find(p => p.id === addProvider)?.enum || 3;
    let cancelled = false;
    setVerifying(true);
    fetch(`${API_BASE_URL}/bank-accounts/verify`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ accountNumber, bankCode: selectedBankCode, provider: providerEnum }),
    })
      .then(async response => {
        const data = await response.json();
        if (cancelled) return;
        if (response.ok && data.verified) {
          setVerifiedName(data.accountName);
        } else {
          showMessage('error', data.message || 'Could not verify account details');
        }
      })
      .catch(() => !cancelled && showMessage('error', 'Account verification failed'))
      .finally(() => !cancelled && setVerifying(false));

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountNumber, selectedBankCode, addProvider]);

  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount);
    if (!amount || amount < LIMITS.MIN_DEPOSIT || amount > LIMITS.MAX_DEPOSIT) {
      showMessage('error', `Deposit must be between ${formatNaira(LIMITS.MIN_DEPOSIT)} and ${formatNaira(LIMITS.MAX_DEPOSIT)}`);
      return;
    }

    setDepositSubmitting(true);
    try {
      const providerEnum = PROVIDERS.find(p => p.id === depositProvider)?.enum || 3;
      const response = await fetch(`${API_BASE_URL}/payment/deposit`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          amount,
          provider: providerEnum,
          description: `Deposit via ${depositProvider}`,
          metadata: { userId: String(user.id), platform: 'web' },
        }),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        const paymentUrl = data.transaction?.paymentUrl || data.paymentUrl;
        if (paymentUrl) {
          // Hand off to the provider's checkout page
          window.location.href = paymentUrl;
          return;
        }
        showMessage('success', data.message || 'Deposit initiated successfully');
        setDepositAmount('');
        loadWallet();
        loadTransactions();
      } else {
        showMessage('error', data.message || 'Deposit failed. Please try again.');
      }
    } catch (error) {
      console.error('Deposit error:', error);
      showMessage('error', 'Network error. Please try again.');
    } finally {
      setDepositSubmitting(false);
    }
  };

  const handleSetPin = async () => {
    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      showMessage('error', 'PIN must be exactly 4 digits');
      return;
    }
    if (newPin !== confirmPin) {
      showMessage('error', 'PINs do not match');
      return;
    }

    setPinSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/wallet/${user.id}/set-pin`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ pin: newPin }),
      });
      const data = await response.json();
      if (response.ok) {
        showMessage('success', 'Wallet PIN set successfully');
        setNewPin('');
        setConfirmPin('');
        loadWallet();
      } else {
        showMessage('error', data.message || 'Failed to set PIN');
      }
    } catch (error) {
      console.error('Set PIN error:', error);
      showMessage('error', 'Network error. Please try again.');
    } finally {
      setPinSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount < LIMITS.MIN_WITHDRAWAL || amount > LIMITS.MAX_WITHDRAWAL) {
      showMessage('error', `Withdrawal must be between ${formatNaira(LIMITS.MIN_WITHDRAWAL)} and ${formatNaira(LIMITS.MAX_WITHDRAWAL)}`);
      return;
    }
    if (!withdrawAccountId) {
      showMessage('error', 'Select a bank account (add one under Payment Methods)');
      return;
    }
    if (withdrawPin.length !== 4) {
      showMessage('error', 'Enter your 4-digit wallet PIN');
      return;
    }

    const account = bankAccounts.find(a => String(a.id) === withdrawAccountId);

    setWithdrawSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/payment/withdraw`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          amount,
          provider: account?.provider || 3,
          bankAccountId: Number(withdrawAccountId),
          pin: withdrawPin,
          description: `Withdrawal to ${account?.bankName || 'bank account'}`,
          metadata: { userId: String(user.id), platform: 'web' },
        }),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        showMessage('success', data.message || 'Withdrawal initiated — funds will be transferred shortly');
        setWithdrawAmount('');
        setWithdrawPin('');
        loadWallet();
        loadTransactions();
      } else {
        showMessage('error', data.message || 'Withdrawal failed. Please try again.');
      }
    } catch (error) {
      console.error('Withdrawal error:', error);
      showMessage('error', 'Network error. Please try again.');
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  const handleAddAccount = async () => {
    if (!verifiedName) return;
    const bank = banks.find(b => String(b.code) === String(selectedBankCode));
    const providerEnum = PROVIDERS.find(p => p.id === addProvider)?.enum || 3;

    setAddSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/bank-accounts`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          accountNumber,
          bankCode: String(selectedBankCode),
          bankName: bank?.name || '',
          provider: providerEnum,
          setAsDefault,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        showMessage('success', data.message || 'Bank account added successfully');
        setShowAddAccount(false);
        setAccountNumber('');
        setSelectedBankCode('');
        setVerifiedName('');
        setSetAsDefault(false);
        loadBankAccounts();
      } else {
        showMessage('error', data.message || 'Failed to add bank account');
      }
    } catch (error) {
      console.error('Add account error:', error);
      showMessage('error', 'Network error. Please try again.');
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleSetDefault = async (accountId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/bank-accounts/${accountId}/set-default`, {
        method: 'PUT',
        headers: authHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        showMessage('success', 'Default account updated');
        loadBankAccounts();
      } else {
        showMessage('error', data.message || 'Failed to update default account');
      }
    } catch (error) {
      console.error('Set default error:', error);
      showMessage('error', 'Network error. Please try again.');
    }
  };

  const handleDeleteAccount = async (account) => {
    if (!window.confirm(`Remove ${account.bankName} - ${account.accountNumber}?`)) return;
    try {
      const response = await fetch(`${API_BASE_URL}/bank-accounts/${account.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        showMessage('success', 'Bank account removed');
        loadBankAccounts();
      } else {
        showMessage('error', data.message || 'Failed to remove bank account');
      }
    } catch (error) {
      console.error('Delete account error:', error);
      showMessage('error', 'Network error. Please try again.');
    }
  };

  // ---------- Shared styles ----------
  const card = {
    backgroundColor: '#1f2937',
    border: '1px solid #374151',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '16px',
  };

  const input = {
    width: '100%',
    backgroundColor: '#111827',
    border: '1px solid #374151',
    borderRadius: '8px',
    padding: '12px',
    color: 'white',
    fontSize: '16px',
    boxSizing: 'border-box',
  };

  const label = {
    display: 'block',
    fontSize: '13px',
    color: '#9ca3af',
    marginBottom: '6px',
    fontWeight: '500',
  };

  const primaryButton = (disabled) => ({
    width: '100%',
    background: disabled ? '#374151' : 'linear-gradient(135deg, #7c3aed, #2563eb)',
    border: 'none',
    borderRadius: '8px',
    padding: '14px',
    color: 'white',
    fontSize: '16px',
    fontWeight: '600',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  });

  const providerSelector = (value, onChange) => (
    <div style={{ display: 'flex', gap: '12px' }}>
      {PROVIDERS.map(provider => (
        <button
          key={provider.id}
          onClick={() => onChange(provider.id)}
          style={{
            flex: 1,
            backgroundColor: value === provider.id ? 'rgba(124, 58, 237, 0.2)' : '#111827',
            border: `1px solid ${value === provider.id ? '#7c3aed' : '#374151'}`,
            borderRadius: '8px',
            padding: '12px',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          {provider.name}
        </button>
      ))}
    </div>
  );

  // ---------- Tabs ----------
  const renderDeposit = () => (
    <div style={card}>
      <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>Deposit Funds</h3>

      <div style={{ marginBottom: '16px' }}>
        <label style={label}>Amount (₦)</label>
        <input
          type="number"
          min={LIMITS.MIN_DEPOSIT}
          max={LIMITS.MAX_DEPOSIT}
          value={depositAmount}
          onChange={e => setDepositAmount(e.target.value)}
          placeholder={`Min ${formatNaira(LIMITS.MIN_DEPOSIT)}`}
          style={input}
        />
        <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
          {QUICK_AMOUNTS.map(amount => (
            <button
              key={amount}
              onClick={() => setDepositAmount(String(amount))}
              style={{
                backgroundColor: '#111827',
                border: '1px solid #374151',
                borderRadius: '8px',
                padding: '8px 14px',
                color: '#d1d5db',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              {formatNaira(amount)}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={label}>Payment Provider</label>
        {providerSelector(depositProvider, setDepositProvider)}
      </div>

      <button onClick={handleDeposit} disabled={depositSubmitting} style={primaryButton(depositSubmitting)}>
        {depositSubmitting ? <RefreshCw size={18} className="spin" /> : <ArrowDownCircle size={18} />}
        {depositSubmitting ? 'Processing...' : 'Deposit'}
      </button>

      <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '12px', marginBottom: 0 }}>
        You'll be redirected to {PROVIDERS.find(p => p.id === depositProvider)?.name} to complete payment securely.
      </p>
    </div>
  );

  const renderWithdraw = () => {
    if (wallet && wallet.hasPIN === false) {
      return (
        <div style={card}>
          <h3 style={{ margin: '0 0 8px', fontSize: '18px' }}>Set Your Wallet PIN</h3>
          <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '16px' }}>
            Withdrawals require a 4-digit PIN. Set one to continue.
          </p>
          <div style={{ marginBottom: '12px' }}>
            <label style={label}>New PIN</label>
            <input
              type="password" inputMode="numeric" maxLength={4}
              value={newPin}
              onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))}
              placeholder="••••" style={input}
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={label}>Confirm PIN</label>
            <input
              type="password" inputMode="numeric" maxLength={4}
              value={confirmPin}
              onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ''))}
              placeholder="••••" style={input}
            />
          </div>
          <button onClick={handleSetPin} disabled={pinSubmitting} style={primaryButton(pinSubmitting)}>
            {pinSubmitting ? 'Saving...' : 'Set PIN'}
          </button>
        </div>
      );
    }

    return (
      <div style={card}>
        <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>Withdraw Funds</h3>

        <div style={{ marginBottom: '16px' }}>
          <label style={label}>Amount (₦)</label>
          <input
            type="number"
            min={LIMITS.MIN_WITHDRAWAL}
            max={LIMITS.MAX_WITHDRAWAL}
            value={withdrawAmount}
            onChange={e => setWithdrawAmount(e.target.value)}
            placeholder={`Min ${formatNaira(LIMITS.MIN_WITHDRAWAL)}`}
            style={input}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={label}>Bank Account</label>
          {bankAccounts.length === 0 ? (
            <div style={{
              backgroundColor: '#111827', border: '1px dashed #374151', borderRadius: '8px',
              padding: '16px', textAlign: 'center',
            }}>
              <p style={{ fontSize: '13px', color: '#9ca3af', margin: '0 0 10px' }}>
                No bank accounts yet
              </p>
              <button
                onClick={() => { setActiveTab('methods'); setShowAddAccount(true); }}
                style={{
                  backgroundColor: 'rgba(124, 58, 237, 0.2)', border: '1px solid #7c3aed',
                  borderRadius: '8px', padding: '8px 16px', color: 'white', fontSize: '13px',
                  fontWeight: '600', cursor: 'pointer',
                }}
              >
                Add Bank Account
              </button>
            </div>
          ) : (
            <select
              value={withdrawAccountId}
              onChange={e => setWithdrawAccountId(e.target.value)}
              style={{ ...input, appearance: 'auto' }}
            >
              {bankAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.bankName} — {account.accountNumber} ({account.accountName})
                  {account.isDefault ? ' ★' : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={label}>Wallet PIN</label>
          <input
            type="password" inputMode="numeric" maxLength={4}
            value={withdrawPin}
            onChange={e => setWithdrawPin(e.target.value.replace(/\D/g, ''))}
            placeholder="••••" style={input}
          />
        </div>

        <button
          onClick={handleWithdraw}
          disabled={withdrawSubmitting || bankAccounts.length === 0}
          style={primaryButton(withdrawSubmitting || bankAccounts.length === 0)}
        >
          {withdrawSubmitting ? <RefreshCw size={18} /> : <ArrowUpCircle size={18} />}
          {withdrawSubmitting ? 'Processing...' : 'Withdraw'}
        </button>

        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '12px', marginBottom: 0 }}>
          Withdrawals are sent to your verified bank account, typically within 2–24 hours.
        </p>
      </div>
    );
  };

  const renderMethods = () => (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ margin: 0, fontSize: '18px' }}>Withdrawal Accounts</h3>
        <button
          onClick={() => setShowAddAccount(!showAddAccount)}
          style={{
            backgroundColor: 'rgba(124, 58, 237, 0.2)', border: '1px solid #7c3aed',
            borderRadius: '8px', padding: '8px 14px', color: 'white', fontSize: '13px',
            fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          <Plus size={14} /> Add Account
        </button>
      </div>

      {showAddAccount && (
        <div style={card}>
          <h4 style={{ margin: '0 0 16px', fontSize: '15px' }}>Add Bank Account</h4>

          <div style={{ marginBottom: '16px' }}>
            <label style={label}>Provider</label>
            {providerSelector(addProvider, setAddProvider)}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={label}>Bank</label>
            <select
              value={selectedBankCode}
              onChange={e => setSelectedBankCode(e.target.value)}
              disabled={loadingBanks}
              style={{ ...input, appearance: 'auto' }}
            >
              <option value="">{loadingBanks ? 'Loading banks...' : 'Select your bank'}</option>
              {banks.map(bank => (
                <option key={`${bank.code}-${bank.name}`} value={bank.code}>{bank.name}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={label}>Account Number</label>
            <input
              type="text" inputMode="numeric" maxLength={10}
              value={accountNumber}
              onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))}
              placeholder="10-digit account number"
              style={input}
            />
            {verifying && (
              <p style={{ fontSize: '13px', color: '#9ca3af', margin: '8px 0 0' }}>Verifying account...</p>
            )}
            {verifiedName && (
              <p style={{
                fontSize: '14px', color: '#10b981', margin: '8px 0 0',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                <CheckCircle size={16} /> {verifiedName}
              </p>
            )}
          </div>

          <label style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            fontSize: '13px', color: '#d1d5db', marginBottom: '20px', cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={setAsDefault}
              onChange={e => setSetAsDefault(e.target.checked)}
            />
            Set as default withdrawal account
          </label>

          <button
            onClick={handleAddAccount}
            disabled={!verifiedName || addSubmitting}
            style={primaryButton(!verifiedName || addSubmitting)}
          >
            {addSubmitting ? 'Adding...' : 'Add Bank Account'}
          </button>
        </div>
      )}

      {bankAccounts.length === 0 && !showAddAccount ? (
        <div style={{ ...card, textAlign: 'center', padding: '40px 20px' }}>
          <CreditCard size={48} color="#6b7280" style={{ marginBottom: '12px' }} />
          <h4 style={{ margin: '0 0 8px' }}>No Bank Accounts</h4>
          <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
            Add a bank account to start receiving withdrawals
          </p>
        </div>
      ) : (
        bankAccounts.map(account => (
          <div key={account.id} style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '22px',
                backgroundColor: 'rgba(124, 58, 237, 0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <CreditCard size={20} color="#a78bfa" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '600', fontSize: '15px' }}>{account.bankName}</div>
                <div style={{ fontSize: '13px', color: '#9ca3af' }}>{account.accountNumber}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{account.accountName}</div>
                <div style={{ fontSize: '11px', color: '#a78bfa' }}>via {account.providerName}</div>
              </div>
              {account.isDefault && (
                <span style={{
                  backgroundColor: '#7c3aed', borderRadius: '10px', padding: '4px 10px',
                  fontSize: '11px', fontWeight: '600',
                }}>
                  Default
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
              {!account.isDefault && (
                <button
                  onClick={() => handleSetDefault(account.id)}
                  style={{
                    flex: 1, backgroundColor: 'rgba(124, 58, 237, 0.15)', border: 'none',
                    borderRadius: '8px', padding: '10px', color: '#a78bfa', fontSize: '13px',
                    fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: '6px',
                  }}
                >
                  <Star size={14} /> Set Default
                </button>
              )}
              <button
                onClick={() => handleDeleteAccount(account)}
                style={{
                  flex: account.isDefault ? 1 : undefined,
                  backgroundColor: 'rgba(239, 68, 68, 0.12)', border: 'none',
                  borderRadius: '8px', padding: '10px 16px', color: '#ef4444', fontSize: '13px',
                  fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '6px',
                }}
              >
                <Trash2 size={14} /> Remove
              </button>
            </div>
          </div>
        ))
      )}
    </>
  );

  const renderTransactions = () => (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ margin: 0, fontSize: '18px' }}>Transaction History</h3>
        <button
          onClick={loadTransactions}
          style={{
            backgroundColor: 'transparent', border: '1px solid #374151', borderRadius: '8px',
            padding: '8px 12px', color: '#9ca3af', fontSize: '13px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {loadingTransactions ? (
        <div style={{ ...card, textAlign: 'center', color: '#9ca3af' }}>Loading transactions...</div>
      ) : transactions.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: '40px 20px' }}>
          <Clock size={48} color="#6b7280" style={{ marginBottom: '12px' }} />
          <h4 style={{ margin: '0 0 8px' }}>No Transactions Yet</h4>
          <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
            Your deposits and withdrawals will appear here
          </p>
        </div>
      ) : (
        transactions.map(transaction => {
          // Money in (credit): deposits, bet winnings, refunds. Everything else is out.
          const isCredit = ['deposit', 'bet_winnings', 'refund'].includes(transaction.type);
          const typeLabel = {
            bet_placement: 'Bet Placed',
            bet_winnings: 'Bet Winnings',
            refund: 'Refund',
          }[transaction.type] || transaction.type;
          return (
            <div
              key={transaction.id}
              style={{ ...card, padding: '16px', display: 'flex', alignItems: 'center', gap: '14px' }}
            >
              <div style={{
                width: '40px', height: '40px', borderRadius: '20px', flexShrink: 0,
                backgroundColor: isCredit ? 'rgba(16, 185, 129, 0.12)' : 'rgba(245, 158, 11, 0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {isCredit
                  ? <ArrowDownCircle size={20} color="#10b981" />
                  : <ArrowUpCircle size={20} color="#f59e0b" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '600', fontSize: '14px', textTransform: 'capitalize' }}>
                  {typeLabel}
                </div>
                <div style={{
                  fontSize: '12px', color: '#9ca3af',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {transaction.description || transaction.reference}
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>
                  {new Date(transaction.createdAt).toLocaleString()}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  fontWeight: '700', fontSize: '15px',
                  color: isCredit ? '#10b981' : '#f59e0b',
                }}>
                  {isCredit ? '+' : '-'}{formatNaira(transaction.amount)}
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'capitalize' }}>
                  {transaction.status}
                </div>
              </div>
            </div>
          );
        })
      )}
    </>
  );

  const tabs = [
    { id: 'deposit', name: 'Deposit' },
    { id: 'withdraw', name: 'Withdraw' },
    { id: 'methods', name: 'Payment Methods' },
    { id: 'transactions', name: 'Transactions' },
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#000', color: 'white' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
      }}>
        <button
          onClick={onBack}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)', border: 'none', borderRadius: '8px',
            padding: '8px', color: 'white', cursor: 'pointer', display: 'flex',
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Wallet size={20} /> Wallet
          </h1>
          <p style={{ fontSize: '13px', margin: '2px 0 0', opacity: 0.9 }}>
            Balance: {formatNaira(wallet?.availableBalance ?? wallet?.balance ?? user?.balance)}
          </p>
        </div>
      </div>

      {/* Feedback banner */}
      {message && (
        <div style={{
          margin: '16px 20px 0',
          padding: '12px 16px',
          borderRadius: '8px',
          backgroundColor: message.type === 'success' ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
          border: `1px solid ${message.type === 'success' ? '#10b981' : '#ef4444'}`,
          color: message.type === 'success' ? '#10b981' : '#ef4444',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', padding: '16px 20px 0', flexWrap: 'wrap' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              backgroundColor: activeTab === tab.id ? 'rgba(124, 58, 237, 0.2)' : 'transparent',
              border: `1px solid ${activeTab === tab.id ? '#7c3aed' : '#374151'}`,
              borderRadius: '20px',
              padding: '8px 16px',
              color: activeTab === tab.id ? 'white' : '#9ca3af',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            {tab.name}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '20px', maxWidth: '560px', margin: '0 auto' }}>
        {activeTab === 'deposit' && renderDeposit()}
        {activeTab === 'withdraw' && renderWithdraw()}
        {activeTab === 'methods' && renderMethods()}
        {activeTab === 'transactions' && renderTransactions()}
      </div>
    </div>
  );
};

export default WalletPage;
